import { createContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { StorageService } from '../services/storage';
import { STORAGE_KEYS } from '../constants/storageKeys';

const AuthContext = createContext();
const EXPIRATION_TIME = 1000 * 60 * 60 * 24 * 30; // 30 days
const AUTO_LOGOUT_INTERVAL = 1000 * 60 * 5; // 5 minutes

const AuthProvider = ({ children }) => {
	const [isAuthenticated, setIsAuthenticated] = useState(false);
	const [user, setUser] = useState(null);
	const [token, setToken] = useState(null);
	const [loading, setLoading] = useState(true);
	const [timerTicker, setTimerTicker] = useState(0);

	const authenticate = (user, token, rememberMe = false) => {
		setUser(user);
		setToken(token);
		if (rememberMe) {
			StorageService.setItem(STORAGE_KEYS.AUTH_TOKEN, token, new Date().getTime() + EXPIRATION_TIME);
			StorageService.setItem(STORAGE_KEYS.USER, user, new Date().getTime() + EXPIRATION_TIME);
		} else {
			StorageService.setItem(STORAGE_KEYS.AUTH_TOKEN, token);
			StorageService.setItem(STORAGE_KEYS.USER, user);
		}

		setIsAuthenticated(true);
	};

	const logout = () => {
		StorageService.removeItem(STORAGE_KEYS.AUTH_TOKEN);
		StorageService.removeItem(STORAGE_KEYS.USER);

		setIsAuthenticated(false);
	};

	const checkTokenExpiration = async () => {
		const token = await StorageService.getItem(STORAGE_KEYS.AUTH_TOKEN);

		if (token) {
			// Toggle timerTicker
			setTimerTicker(1 - timerTicker);
		} else {
			console.log('Auto logout triggered');
			logout();
		}
	};

	useEffect(() => {
		// Check if user is authenticated (e.g., by checking a token in localStorage)
		const checkAuth = () => {
			const token = StorageService.getItem(STORAGE_KEYS.AUTH_TOKEN);
			const user = StorageService.getItem(STORAGE_KEYS.USER);
			setIsAuthenticated(!!token);
			setToken(token);
			setUser(user);
			setLoading(false);
		};

		checkAuth();
	}, []);

	useEffect(() => {
		const interval = setInterval(() => checkTokenExpiration(), AUTO_LOGOUT_INTERVAL);
		return () => clearInterval(interval);
	}, [timerTicker]);

	return (
		<AuthContext.Provider
			value={{ isAuthenticated, loading, authenticate, logout, user, token, timerTicker }}
		>
			{children}
		</AuthContext.Provider>
	);
};

AuthProvider.propTypes = {
	children: PropTypes.node.isRequired,
};

export { AuthProvider, AuthContext };
